import React from "react";
import {
    Box,
    Container,
    Typography,
    Button,
    Grid,
    Fade,
    Stack,
} from "@mui/material";
import {
    Android as AndroidIcon,
    Apple as AppleIcon,
} from "@mui/icons-material";
import { PhoneMockup } from "../ScreenshotGallery/ScreenshotGallery";
import { ANDROID_APP_URL, IOS_APP_URL, SITE_NAME } from "../../seo/constants";
import { HERO_SCREENSHOT, APP_SCREENSHOTS } from "../../seo/assets";

const HeroSection = () => {
    return (
        <Box
            sx={{
                background:
                    "linear-gradient(135deg, rgba(240, 248, 240, 0.9) 0%, rgba(255, 255, 255, 1) 55%, rgba(218, 165, 32, 0.06) 100%)",
                py: { xs: 6, md: 10, lg: 12 },
                position: "relative",
                overflow: "hidden",
                "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `
            radial-gradient(circle at 20% 20%, rgba(46, 139, 87, 0.06) 0%, transparent 45%),
            radial-gradient(circle at 80% 70%, rgba(218, 165, 32, 0.07) 0%, transparent 45%)
          `,
                    pointerEvents: "none",
                },
            }}
        >
            <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
                <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <Fade in timeout={900}>
                            <Box>
                                <Typography
                                    variant="overline"
                                    sx={{
                                        color: "primary.main",
                                        fontWeight: 700,
                                        letterSpacing: 1.4,
                                        display: "block",
                                        mb: 1.5,
                                        textAlign: { xs: "center", md: "left" },
                                    }}
                                >
                                    {SITE_NAME} — Free on App Store & Google Play
                                </Typography>
                                <Typography
                                    variant="h1"
                                    component="h1"
                                    sx={{
                                        mb: 3,
                                        fontWeight: 700,
                                        fontSize: {
                                            xs: "2.25rem",
                                            sm: "2.75rem",
                                            md: "3.25rem",
                                        },
                                        background:
                                            "linear-gradient(135deg, #2C3E2D 0%, #2E8B57 100%)",
                                        WebkitBackgroundClip: "text",
                                        WebkitTextFillColor: "transparent",
                                        backgroundClip: "text",
                                        textAlign: { xs: "center", md: "left" },
                                    }}
                                >
                                    AI Quran Companion for{" "}
                                    <Box
                                        component="span"
                                        sx={{
                                            background:
                                                "linear-gradient(135deg, #2E8B57 0%, #DAA520 100%)",
                                            WebkitBackgroundClip: "text",
                                            WebkitTextFillColor: "transparent",
                                            backgroundClip: "text",
                                        }}
                                    >
                                        Prayer Times & Deen
                                    </Box>
                                </Typography>

                                <Typography
                                    variant="body1"
                                    sx={{
                                        mb: 4,
                                        fontSize: {
                                            xs: "1.05rem",
                                            sm: "1.125rem",
                                        },
                                        lineHeight: 1.75,
                                        color: "text.secondary",
                                        maxWidth: 560,
                                        textAlign: { xs: "center", md: "left" },
                                        mx: { xs: "auto", md: 0 },
                                    }}
                                >
                                    Learn the Holy Quran with AI tafsir, never miss
                                    Salah with accurate prayer times and Qibla,
                                    receive daily ayat reminders, and grow closer
                                    to Allah — built for Muslims, students, and
                                    families.
                                </Typography>

                                <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    spacing={2}
                                    sx={{
                                        mb: 2,
                                        alignItems: "center",
                                        justifyContent: {
                                            xs: "center",
                                            md: "flex-start",
                                        },
                                    }}
                                >
                                    <Button
                                        variant="contained"
                                        size="large"
                                        startIcon={<AppleIcon />}
                                        href={IOS_APP_URL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                            minWidth: { xs: "100%", sm: 210 },
                                            maxWidth: { xs: 320, sm: "none" },
                                            fontWeight: 600,
                                        }}
                                    >
                                        Download for iOS
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="large"
                                        startIcon={<AndroidIcon />}
                                        href={ANDROID_APP_URL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                            minWidth: { xs: "100%", sm: 210 },
                                            maxWidth: { xs: 320, sm: "none" },
                                            fontWeight: 600,
                                            borderWidth: 2,
                                            "&:hover": { borderWidth: 2 },
                                        }}
                                    >
                                        Download for Android
                                    </Button>
                                </Stack>

                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: "text.secondary",
                                        textAlign: { xs: "center", md: "left" },
                                    }}
                                >
                                    Quran · Salah · Qibla · Daily Ayat · Islamic AI Chat
                                </Typography>
                            </Box>
                        </Fade>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "flex-end",
                                gap: { xs: 1.5, sm: 2 },
                                position: "relative",
                                minHeight: { xs: 360, md: 480 },
                            }}
                        >
                            <Box
                                sx={{
                                    display: { xs: "none", sm: "block" },
                                    transform: "translateY(24px) rotate(-8deg)",
                                    opacity: 0.85,
                                    filter: "brightness(0.97)",
                                }}
                            >
                                <PhoneMockup
                                    src={APP_SCREENSHOTS[1].src}
                                    alt={APP_SCREENSHOTS[1].alt}
                                    maxWidth={180}
                                />
                            </Box>
                            <Box
                                sx={{
                                    position: "relative",
                                    zIndex: 2,
                                    transform: {
                                        xs: "none",
                                        sm: "translateY(-8px)",
                                    },
                                }}
                            >
                                <PhoneMockup
                                    src={HERO_SCREENSHOT.src}
                                    alt={HERO_SCREENSHOT.alt}
                                    maxWidth={240}
                                />
                            </Box>
                            <Box
                                sx={{
                                    display: { xs: "none", sm: "block" },
                                    transform: "translateY(24px) rotate(8deg)",
                                    opacity: 0.85,
                                    filter: "brightness(0.97)",
                                }}
                            >
                                <PhoneMockup
                                    src={APP_SCREENSHOTS[2].src}
                                    alt={APP_SCREENSHOTS[2].alt}
                                    maxWidth={180}
                                />
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default HeroSection;
